import Anthropic from '@anthropic-ai/sdk'
import { ADVISOR_RELAY_URL } from '../config'

// The SDK's ClientOptions.fetch accepts a function with this shape.
// We define it locally rather than pulling from the SDK's internal path
// (not exposed via package.json exports), and it is structurally identical
// to `typeof fetch` from the DOM lib.
type FetchFn = (input: string | URL | Request, init?: RequestInit) => Promise<Response>

/**
 * Returns an async fetch that injects a fresh Auth0 bearer token on every
 * request.  Caller headers (incl. content-type, anthropic-version,
 * anthropic-beta, and the SDK's placeholder x-api-key) are preserved;
 * Authorization is overwritten with the token returned by `getToken`.
 */
export function makeRelayFetch(
  getToken: () => Promise<string>,
  baseFetch: FetchFn = fetch,
): FetchFn {
  return async (url: string | URL | Request, init?: RequestInit) => {
    const headers = new Headers(init?.headers)
    headers.set('Authorization', 'Bearer ' + (await getToken()))
    return baseFetch(url, { ...init, headers })
  }
}

/**
 * Creates an Anthropic SDK client that routes every request through the
 * advisor relay.  The relay validates the Auth0 bearer injected by
 * `makeRelayFetch` and forwards to Anthropic — no real API key ever
 * touches the browser.
 */
export function createAdvisorClient(getToken: () => Promise<string>): Anthropic {
  return new Anthropic({
    baseURL: ADVISOR_RELAY_URL,
    apiKey: 'unused',
    dangerouslyAllowBrowser: true,
    fetch: makeRelayFetch(getToken),
  })
}

/**
 * Same as `createAdvisorClient` but accepts an explicit `fetchImpl` for
 * testing (avoids relying on the real network).
 */
export function createAdvisorClientWith(
  getToken: () => Promise<string>,
  fetchImpl: FetchFn,
): Anthropic {
  return new Anthropic({
    baseURL: ADVISOR_RELAY_URL,
    apiKey: 'unused',
    dangerouslyAllowBrowser: true,
    fetch: makeRelayFetch(getToken, fetchImpl),
  })
}
